import type {
  DispatchStateOverrides,
  DispatchViewStatePatch,
  ResolvedDispatchQuery,
} from "@storyboard/dispatch-view/resolve";
import { inactiveControlValue, inactiveControlValues } from "@storyboard/dispatch-view/resolve";
import type { DispatchSort } from "@storyboard/dispatch-view/view";
import { NONE } from "@storyboard/dispatch-view/view";
import type { FilterNode } from "@storyboard/driver-query/predicate";
import type { QueryChip } from "@/components/Dispatch/DispatchQueryStrip";
import { type ConditionNaming, describeCondition, describeControl } from "./adhocConditions";

/**
 * What the strip says about a resolved query: one chip per active control, one per top-level ad-hoc condition, and
 * one for a sort the state lays over the view's.
 *
 * Each chip's clear is a patch on the view's state, sent to `write`: a control chip switches its control off, an ad-hoc
 * chip drops that condition (an empty list when it was the last, so the conditions read as none rather than
 * inheriting), the sort chip writes the view's own sort back (none where the view has none, which reads "Unsorted").
 * Every clear
 * writes a value rather than `null`: `null` would inherit the layer beneath, and a workspace's default state there
 * would bring the chip straight back.
 *
 * @param viewSort - The view's own sort, what clearing the sort chip returns to.
 */
export function buildQueryChips(
  query: ResolvedDispatchQuery,
  overrides: Pick<DispatchStateOverrides, "sort">,
  naming: ConditionNaming,
  write: (patch: DispatchViewStatePatch) => void,
  viewSort: DispatchSort | undefined,
): QueryChip[] {
  const fromControls = query.controls.flatMap((control): QueryChip[] => {
    const text = describeControl(control, query.controlValues[control.id], naming);
    if (!text) return [];
    const onClear = () => write({ query: { controls: { [control.id]: inactiveControlValue(control) } } });
    return [{ ...text, key: `control:${control.id}`, source: "control", onClear }];
  });
  const fromAdhoc = query.adhoc.map(
    (condition, index): QueryChip => ({
      ...describeCondition(condition, naming),
      key: `adhoc:${index}`,
      source: "adhoc",
      onClear: () => write({ query: { adhoc: withoutCondition(query.adhoc, index) } }),
    }),
  );
  return [...fromControls, ...fromAdhoc, ...sortChips(query.sort, overrides.sort, naming, write, viewSort)];
}

/** The conditions with one dropped, as the list to store: empty once none remain, since `null` would inherit. */
function withoutCondition(conditions: readonly FilterNode[], index: number): FilterNode[] {
  return conditions.filter((_, other) => other !== index);
}

function sortChips(
  sort: ResolvedDispatchQuery["sort"],
  overridden: boolean,
  naming: ConditionNaming,
  write: (patch: DispatchViewStatePatch) => void,
  viewSort: DispatchSort | undefined,
): QueryChip[] {
  if (!overridden) return [];
  const arrow = sort?.direction === "asc" ? "↑" : "↓";
  const word = sort?.direction === "asc" ? "ascending" : "descending";
  return [
    {
      key: "sort",
      source: "display",
      summary: sort ? `Sorted by ${naming.labelFor(sort.field)} ${arrow}` : "Unsorted",
      detail: sort
        ? `Sorted by ${naming.labelFor(sort.field)}, ${word}; the view sorts differently`
        : "Left in the order the data arrives; the view sorts these rows",
      onClear: () => write({ query: { sort: viewSort ?? NONE } }),
    },
  ];
}

/** How many chips hide rows: every chip but a display one, since a sort reorders without hiding anything. */
export function narrowingCount(chips: readonly QueryChip[]): number {
  return chips.filter((chip) => chip.source !== "display").length;
}

/**
 * The patch "Clear all" writes: every control off, no conditions, and the view's own sort back.
 *
 * @param viewSort - The view's own sort; none where the view has none.
 */
export function clearAllPatch(
  query: Pick<ResolvedDispatchQuery, "controls">,
  viewSort: DispatchSort | undefined,
): DispatchViewStatePatch {
  return { query: { controls: inactiveControlValues(query.controls), adhoc: [], sort: viewSort ?? NONE } };
}
